import type { ConnectorConnection, ConnectorFolder, KnowledgeBase } from '@ralph/contracts'
import { PROVIDER_LABEL, SURFACE_LABEL } from '@ralph/contracts'
import clsx from 'clsx'
import { CircleDashed, Clock, KeyRound, PauseCircle, Plus, TriangleAlert } from 'lucide-react'
import { type CSSProperties, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Notice, riseDelay, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Button, EmptyState, Meter, Spinner } from '@/components/ui/primitives.tsx'
import {
  useCheckNow,
  useConnections,
  useFolders,
  useRemoveConnection,
  useStartSignIn,
  useViewer
} from '@/hooks/use-connectors.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'
import { ConnectFolderFlow } from './ConnectFolderFlow.tsx'
import {
  canRenew,
  type FolderStanding,
  folderLine,
  mayUseConnection,
  needsPerson,
  ownerLeft,
  problemSentence,
  standingLabel,
  standingOf
} from './connected-folders.ts'
import { FolderDrawer } from './FolderDrawer.tsx'
import { ProviderMark } from './ProviderMark.tsx'

/**
 * The Connected folders tab (PRD-25), on a knowledge base its maintainers open (PRD-26 D-38).
 *
 * A list of what the base is watching, each row a card: provider glyph, the path, the
 * standing in a word and a glyph (never colour alone — four of the eight states are not
 * faults and are not painted as faults), the account, the four facts a maintainer scans for,
 * and — only when something needs a person — the sentence and the one remedy. The quiet case
 * is a first-class state: "Watching · checked 6m ago · next in 24m" is as present as a failure.
 *
 * The connect flow replaces the tab's content. A sign-in is a top-level navigation; the return
 * lands here with `?connected=<connectionId>` and the flow resumes at the folder step, or with
 * `?connectFailed=` and one sentence that names what did not happen.
 *
 * Remedies that read through a folder's account are offered only to someone who may use it —
 * its owner or an administrator; the server refuses everybody else (D-38).
 */

/**
 * What the browser was told on its way back from a provider; read once, then removed from
 * the address bar THROUGH the router, so no later navigation writes it back and reopens the
 * flow by itself.
 */
const useReturnNotice = (): { connected: string | null; failed: string | null } => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [notice] = useState(() => ({
    connected: searchParams.get('connected'),
    failed: searchParams.get('connectFailed')
  }))
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on arrival — the notice is a snapshot of the first render
  useEffect(() => {
    if (!notice.connected && !notice.failed) return
    const next = new URLSearchParams(searchParams)
    next.delete('connected')
    next.delete('connectFailed')
    setSearchParams(next, { replace: true })
  }, [])
  return notice
}

type TabBase = Pick<KnowledgeBase, 'id' | 'name' | 'access' | 'owner' | 'lentToCount'>

export const ConnectedFoldersTab = ({ base, canManage }: { base: TabBase; canManage: boolean }) => {
  // In the body, never at module scope: a catalogue branch captured at import renders the
  // previous language with no error of any kind (PRD-27 F-3).
  const t = useI18n().t.knowledgeFolders
  const folders = useFolders(base.id)
  const returned = useReturnNotice()
  const [searchParams, setSearchParams] = useSearchParams()
  const [connecting, setConnecting] = useState<{ resumeConnectionId?: string; resumeFolder?: ConnectorFolder } | null>(
    returned.connected ? { resumeConnectionId: returned.connected } : null
  )
  const [accountsOpen, setAccountsOpen] = useState(false)

  const items = folders.data?.items ?? []
  const openId = searchParams.get('folder')
  const open = items.find((f) => f.id === openId) ?? null
  const setOpen = (id: string | null) => {
    const params = new URLSearchParams(searchParams)
    if (id) params.set('folder', id)
    else params.delete('folder')
    setSearchParams(params, { replace: true })
  }

  if (connecting && canManage) {
    return (
      <ConnectFolderFlow
        base={base}
        resumeConnectionId={connecting.resumeConnectionId ?? null}
        resumeFolder={connecting.resumeFolder ?? null}
        onDone={() => setConnecting(null)}
        onCancel={() => setConnecting(null)}
      />
    )
  }

  return (
    <div className="space-y-3">
      {returned.failed && (
        <Notice title={t.accounts.signInFailed} icon={<TriangleAlert size={13} aria-hidden />} role="alert">
          {t.accounts.signInFailedBody(t.accounts.theProvider)}{' '}
          <span className="readout block text-fg-subtle">{returned.failed}</span>
        </Notice>
      )}

      {canManage && (
        <AccountsDisclosure open={accountsOpen} onToggle={() => setAccountsOpen(!accountsOpen)} baseId={base.id} />
      )}

      {folders.isLoading ? (
        <p className="flex items-center gap-2 px-1 text-[13px] text-fg-subtle">
          <Spinner size={12} /> {t.loading}
        </p>
      ) : folders.isError ? (
        <EmptyState title={t.unavailable} body={t.unavailableBody} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t.empty}
          body={t.emptyBody(base.name)}
          action={
            canManage ? (
              <Button
                variant="primary"
                icon={<Plus size={13} strokeWidth={2.4} aria-hidden />}
                onClick={() => setConnecting({})}
              >
                {t.connect}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((folder, index) => (
              <li key={folder.id}>
                <FolderRow
                  folder={folder}
                  style={riseDelay(index)}
                  canManage={canManage}
                  onOpen={() => {
                    if (folder.status === 'previewing') setConnecting({ resumeFolder: folder })
                    else setOpen(folder.id)
                  }}
                />
              </li>
            ))}
          </ul>
          {canManage && (
            <div>
              <Button
                size="sm"
                icon={<Plus size={13} strokeWidth={2.4} aria-hidden />}
                onClick={() => setConnecting({})}
              >
                {t.connect}
              </Button>
            </div>
          )}
        </>
      )}

      {open && <FolderDrawer folder={open} base={base} canManage={canManage} onClose={() => setOpen(null)} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A row
// ---------------------------------------------------------------------------

const StandingMark = ({ standing }: { standing: FolderStanding }) => {
  switch (standing) {
    case 'watching':
      return <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-grounded" aria-hidden />
    case 'loading':
    case 'counting':
      return <Spinner size={11} className="text-primary" />
    case 'not_started':
      return <span className="h-[7px] w-[7px] shrink-0 rounded-full border border-rule-strong" aria-hidden />
    case 'paused':
      return <PauseCircle size={12} strokeWidth={2.2} className="shrink-0 text-fg-muted" aria-hidden />
    case 'needs_sign_in':
      return <KeyRound size={12} strokeWidth={2.2} className="shrink-0 text-alert" aria-hidden />
    case 'slowed':
      return <Clock size={12} strokeWidth={2.2} className="shrink-0 text-fg-muted" aria-hidden />
    case 'failed':
    case 'stopped':
      return <TriangleAlert size={12} strokeWidth={2.2} className="shrink-0 text-alert" aria-hidden />
  }
}

export const FolderRow = ({
  folder,
  style,
  canManage,
  onOpen
}: {
  folder: ConnectorFolder
  style?: CSSProperties
  canManage: boolean
  onOpen: () => void
}) => {
  // Inside the component body, never at module scope: a capture at import freezes the locale
  // and then renders the previous language with no error of any kind (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const standing = standingOf(folder)
  const problem = problemSentence(fmt, folder)
  const signIn = useStartSignIn()
  const viewer = useViewer()
  const surface = SURFACE_LABEL[folder.surface]
  const loading = standing === 'loading' && folder.stats.included > 0
  const mayRenew = canRenew(folder.connection, viewer)
  const mayUse = mayUseConnection(folder.connection, viewer)

  return (
    <StudioRow style={style} className={clsx(needsPerson(standing) && 'border-alert/40')}>
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-tile border border-rule bg-card-solid">
          <ProviderMark surface={folder.surface} size={15} className="text-fg-muted" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <button
              type="button"
              onClick={onOpen}
              className="truncate text-left text-[13.5px] font-semibold tracking-[-0.014em] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
            >
              {folder.displayPath}
            </button>
          </div>
          <p className="readout mt-0.5 truncate text-fg-subtle">
            {surface} · {folder.connection.accountLabel}
          </p>
          {loading ? (
            <Meter
              className="mt-2 max-w-[340px]"
              label={t.detail.loaded}
              value={t.detail.loadedOf(fmt, folder.stats.synced, folder.stats.included)}
              fill={folder.stats.synced / Math.max(1, folder.stats.included)}
              tone="primary"
            />
          ) : (
            <p className="readout mt-1 text-fg-subtle">{folderLine(fmt, folder, standing)}</p>
          )}
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[12.5px] font-medium tracking-[-0.01em]">
          <StandingMark standing={standing} />
          {standingLabel(fmt, folder, standing)}
        </span>
      </div>

      {problem && (
        <div
          className={clsx(
            'mt-3 flex flex-wrap items-start gap-x-3 gap-y-2 rounded-tile px-3 py-2.5',
            // Painted as a fault only when a person has to act; a throttle or a pause is news, not an alarm.
            needsPerson(standing) ? 'bg-alert-soft' : 'bg-card-solid/55'
          )}
        >
          <p className="min-w-[16rem] flex-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
            <span className="block font-semibold text-fg">{problem.title}</span>
            {problem.body}
          </p>
          {canManage && standing === 'needs_sign_in' && !mayRenew && folder.connection.mode === 'delegated' && (
            <span className="text-[12.5px] text-fg-muted">
              {t.standing.ownerMustSignIn(folder.connection.ownerName)}
            </span>
          )}
          {/* Moving a folder off a departed person's sign-in is an administrator's (D-38). */}
          {canManage && viewer.admin && ownerLeft(folder) && folder.connection.provider !== 's3' && (
            <Button
              size="sm"
              variant="primary"
              icon={<KeyRound size={12} aria-hidden />}
              disabled={signIn.isPending}
              onClick={() =>
                signIn.mutate(
                  { provider: folder.connection.provider as 'microsoft' | 'google', sourceId: folder.sourceId },
                  { onSuccess: (started) => window.location.assign(started.authorizeUrl) }
                )
              }
            >
              {t.standing.signInWithMine}
            </Button>
          )}
          {canManage && standing === 'needs_sign_in' && mayRenew && folder.connection.provider !== 's3' && (
            <Button
              size="sm"
              variant="primary"
              icon={<KeyRound size={12} aria-hidden />}
              disabled={signIn.isPending}
              onClick={() =>
                signIn.mutate(
                  {
                    provider: folder.connection.provider as 'microsoft' | 'google',
                    sourceId: folder.sourceId,
                    reconnectConnectionId: folder.connection.id
                  },
                  { onSuccess: (started) => window.location.assign(started.authorizeUrl) }
                )
              }
            >
              {t.accounts.signInAgain}
            </Button>
          )}
          {canManage && mayUse && folder.status === 'previewing' && standing === 'stopped' && (
            <CountAgain folderId={folder.id} />
          )}
          {canManage &&
            folder.status !== 'previewing' &&
            (standing === 'stopped' ||
              standing === 'failed' ||
              (standing === 'needs_sign_in' && folder.connection.provider === 's3')) && (
              <Button size="sm" onClick={onOpen}>
                {t.standing.open}
              </Button>
            )}
        </div>
      )}
      {folder.audience.stale && standing !== 'not_started' && (
        <p className="mt-2 flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-alert">
          <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
          {t.access.widened(folder.sourceName)}
        </p>
      )}
      {standing === 'not_started' && (
        <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          <CircleDashed size={12} strokeWidth={2.2} className="shrink-0" aria-hidden />
          {t.standing.notStartedRow}
        </p>
      )}
    </StudioRow>
  )
}

/** A count that failed is re-run from the row, not from a flow that would show zero files. */
const CountAgain = ({ folderId }: { folderId: string }) => {
  const t = useI18n().t.knowledgeFolders
  const check = useCheckNow()
  return (
    <Button
      size="sm"
      variant="primary"
      disabled={check.isPending}
      onClick={() => check.mutate({ id: folderId, walk: true })}
    >
      {t.standing.countAgain}
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Accounts: a disclosure, not a page
// ---------------------------------------------------------------------------

const AccountsDisclosure = ({ open, onToggle, baseId }: { open: boolean; onToggle: () => void; baseId: string }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const connections = useConnections(true)
  const remove = useRemoveConnection()
  const signIn = useStartSignIn()
  const viewer = useViewer()
  const [confirming, setConfirming] = useState<string | null>(null)
  const items = connections.data?.items ?? []
  const summary =
    items.length === 0
      ? t.accounts.summary(fmt, 0)
      : PROVIDER_ORDER.filter((p) => items.some((c) => c.provider === p))
          .map((p) => `${fmt.formatCount(items.filter((c) => c.provider === p).length)} ${PROVIDER_LABEL[p]}`)
          .join(', ')

  return (
    <div className="rounded-tile border border-rule bg-card-solid/55">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span className="eyebrow">{t.accounts.title}</span>
        <span className="readout text-fg-subtle">{summary}</span>
        <span className="readout ml-auto text-fg-subtle">{open ? t.accounts.hide : t.accounts.show}</span>
      </button>
      {open && (
        <ul className="border-t border-rule px-3 py-2">
          {items.length === 0 && <li className="py-1 text-[12.5px] text-fg-subtle">{t.accounts.summary(fmt, 0)}</li>}
          {items.map((c) => (
            <AccountRow
              key={c.id}
              viewer={viewer}
              connection={c}
              confirming={confirming === c.id}
              onConfirm={() => setConfirming(confirming === c.id ? null : c.id)}
              onRemove={() => {
                setConfirming(null)
                remove.mutate(c.id)
              }}
              onSignInAgain={() =>
                signIn.mutate(
                  { provider: c.provider as 'microsoft' | 'google', sourceId: baseId, reconnectConnectionId: c.id },
                  { onSuccess: (started) => window.location.assign(started.authorizeUrl) }
                )
              }
            />
          ))}
          {remove.isError && (
            <li role="alert" className="py-1 text-[12.5px] text-alert">
              {describeApiError(fmt.t, remove.error)}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

const PROVIDER_ORDER: Array<ConnectorConnection['provider']> = ['microsoft', 'google', 's3']

const AccountRow = ({
  connection,
  confirming,
  onConfirm,
  onRemove,
  onSignInAgain,
  viewer
}: {
  connection: ConnectorConnection
  confirming: boolean
  onConfirm: () => void
  onRemove: () => void
  onSignInAgain: () => void
  viewer: { id: string | null; admin: boolean }
}) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-rule py-2 first:border-t-0">
      <ProviderMark provider={connection.provider} size={13} className="text-fg-muted" />
      <span className="min-w-0 flex-1 text-[13px]">
        <span className="font-medium">{connection.accountLabel}</span>
        <span className="readout ml-2 text-fg-subtle">
          {PROVIDER_LABEL[connection.provider]}
          {connection.mode === 'delegated' ? ` · ${connection.ownerName}` : ` · ${t.accounts.companyAccount}`} ·{' '}
          {t.accounts.usedBy(fmt, connection.folderCount)}
        </span>
        {connection.health === 'needs_reauth' && (
          <span className="ml-2 inline-flex items-center gap-1 text-[12px] text-alert">
            <KeyRound size={11} aria-hidden /> {t.standing.needsSignIn}
          </span>
        )}
      </span>
      <span className="flex items-center gap-1.5">
        {/* Renewing and removing belong to whoever signed in with the account, or an administrator. */}
        {connection.provider !== 's3' && canRenew({ mode: connection.mode, ownerId: connection.ownerId }, viewer) && (
          <Button size="sm" variant="ghost" onClick={onSignInAgain}>
            {t.accounts.signInAgain}
          </Button>
        )}
        {(connection.mine || viewer.admin) &&
          (confirming ? (
            <>
              <span className="text-[12px] text-fg-muted">
                {t.accounts.removeConfirm(fmt, connection.accountLabel, connection.folderCount)}
              </span>
              <Button size="sm" variant="danger" onClick={onRemove}>
                {t.accounts.remove}
              </Button>
              <Button size="sm" variant="ghost" onClick={onConfirm}>
                {t.accounts.keep}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={onConfirm}>
              {t.accounts.remove}
            </Button>
          ))}
      </span>
      <span className="readout w-full text-fg-subtle sm:w-auto">
        {connection.lastVerifiedAt ? t.accounts.signedIn(fmt.formatAgo(connection.lastVerifiedAt)) : ''}
      </span>
    </li>
  )
}
